import { inject, Injectable } from '@angular/core';
import { AuthConfig, AuthService as CompleteAuthService } from '@capturum/auth';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { User } from '@features/user/models/user.model';
import { UserRole } from '@features/user/enums/user-role.enum';
import { ConfirmationService } from '@core/services/confirmation.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@capturum/ui/api';
import { auditTime, map, Observable, of, skipWhile, switchMap } from 'rxjs';
import { BaseDataService } from './base-data.service';
import { BaseDataKey } from '@core/enums/base-data-key.enum';
import { Country } from '@features/leads/enums/country.emum';
import { filter, take, tap } from 'rxjs/operators';
import { InfoDialogComponent } from '@shared/dumb-components/info-dialog/info-dialog.component';
import { DialogService } from 'primeng/dynamicdialog';
import { responseData } from '@capturum/builders/core';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends CompleteAuthService {
  private readonly dialogService = inject(DialogService);

  constructor(
    config: AuthConfig,
    http: HttpClient,
    protected readonly router: Router,
    protected readonly translateService: TranslateService,
    protected readonly confirmationService: ConfirmationService,
    protected readonly toastService: ToastService,
    private readonly baseDataService: BaseDataService
  ) {
    super(config, http);
  }

  public override getUser(): User {
    return super.getUser() as unknown as User;
  }

  public getUserRole(): UserRole {
    if (this.isAdministrator()) {
      return UserRole.ADMINISTRATOR;
    } else if (this.isCountryAdministrator()) {
      return UserRole.COUNTRY_ADMINISTRATOR;
    } else if (this.isDistributor()) {
      return UserRole.DISTRIBUTOR;
    } else if (this.isInstaller()) {
      return UserRole.INSTALLER;
    } else if (this.isTelemarketing()) {
      return UserRole.TELEMARKETING;
    } else {
      return null;
    }
  }

  public isRole(roleKey: UserRole | string): boolean {
    return this.getUserRole() === roleKey;
  }

  public isOneOfRoles(roleKeys: (UserRole | string)[]): boolean {
    return roleKeys.some((key) => {
      return this.isRole(key);
    });
  }

  public isAdministrator(): boolean {
    const currentlyLoggedInUser = this.getUser();

    return currentlyLoggedInUser?.currentRole?.key === UserRole.ADMINISTRATOR;
  }

  public isCountryAdministrator(): boolean {
    const currentlyLoggedInUser = this.getUser();

    return currentlyLoggedInUser?.currentRole?.key === UserRole.COUNTRY_ADMINISTRATOR;
  }

  public isInstaller(): boolean {
    const currentlyLoggedInUser = this.getUser();

    return currentlyLoggedInUser?.currentRole?.key === UserRole.INSTALLER;
  }

  public isDistributor(): boolean {
    const currentlyLoggedInUser = this.getUser();

    return currentlyLoggedInUser?.currentRole?.key === UserRole.DISTRIBUTOR;
  }

  public isTelemarketing(): boolean {
    const currentlyLoggedInUser = this.getUser();

    return currentlyLoggedInUser?.currentRole?.key === UserRole.TELEMARKETING;
  }

  public isCountryBenelux(): Observable<boolean> {
    return this.baseDataService.findByValue(BaseDataKey.COUNTRY, Country.BENELUX).pipe(
      map((country) => {
        return country && this.getUser()?.country_base_data_value_id === country.id;
      })
    );
  }

  public handleSuccessfulLogin(response: any): void {
    if (response.method) {
      this.router.navigate(['/', 'auth', 'two-factor'], {
        queryParams: {
          method: response.method,
          userId: response.user_id,
        },
      });

      return;
    }

    const user = response.user;

    of(response)
      .pipe(
        skipWhile((res) => {
          return !res;
        }),
        take(1),
        auditTime(1500),
        tap(() => {
          const isLowActivity =
            !user?.installer_active && user.substatus === 'automatically_less_than_50_percent_quoted';
          const shouldShowActivation = !user.installer_active && user.agreed_at && !user.installer_suspended;

          if (isLowActivity) {
            this.showInfoDialog();
          } else if (shouldShowActivation) {
            this.showActivationConfirm(user);
          }
        }),
        filter(() => {
          return !user?.installer_active;
        }),
        switchMap(() => {
          return this.checkCategoryChange(user.id);
        }),
        filter((data) => {
          return data?.hasOwnProperty('welcome_line');
        }),
        tap((data) => {
          this.dialogService.open(InfoDialogComponent, {
            header: this.translateService.instant('builders.confirmation.title'),
            width: '540px',
            data: {
              message: `${data.welcome_line} \n\n ${data.main_info_line} \n\n ${data.second_info_line}`,
            },
          });
        })
      )
      .subscribe();
  }

  private showActivationConfirm(user: User): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('builders.confirmation.title'),
      message: this.translateService.instant('LeadPortal.user.confirm-activation.message'),
      accept: () => {
        return this.activateInstaller(user);
      },
    });
  }

  private showInfoDialog(): void {
    this.dialogService.open(InfoDialogComponent, {
      header: this.translateService.instant('builders.confirmation.title'),
      width: '540px',
      data: {
        message: this.translateService.instant('LeadPortal.user.inactive-50-percent-popup-warning.message'),
      },
    });
  }

  private activateInstaller(user: any): void {
    this.http
      .post(`${this.config.baseUrl}/user/${user.id}/activate`, {}, { headers: this.getHeaders() })
      .subscribe(() => {
        this.toastService.success(
          this.translateService.instant('toast.success.title'),
          this.translateService.instant('LeadPortal.user.activation.toast.message')
        );
      });
  }

  private checkCategoryChange(
    userId: string
  ): Observable<{ welcome_line: string; main_info_line: string; second_info_line: string }> {
    return this.http
      .get(`${this.config.baseUrl}/user/${userId}/check-category-change`, { headers: this.getHeaders() })
      .pipe(responseData);
  }
}
